import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, ExternalLink, AlertTriangle, Pencil, Send, Copy, Eye, Clock, CheckCircle2 } from "lucide-react";
import { Estimate, isNewFormat, SubLine, LineItem, useUpdateEstimate } from "@/hooks/useEstimates";
import { QBEstimate } from "@/hooks/useQBEstimates";
import { SendEstimateDialog } from "./SendEstimateDialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

const fmt = (n: number | null | undefined) =>
  n != null ? `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—";

const safeDate = (d: string | null) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return "—"; }
};

const safeDateFull = (d: string | null) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return "—"; }
};

const statusClass: Record<string, string> = {
  Draft: "bg-muted text-muted-foreground",
  Sent: "bg-blue-100 text-blue-800",
  Accepted: "bg-emerald-100 text-emerald-800",
  Rejected: "bg-destructive/15 text-destructive",
  Closed: "bg-muted text-muted-foreground",
  Pending: "bg-yellow-100 text-yellow-800",
};

const syncDotClass: Record<string, string> = {
  synced: "bg-emerald-500", pending: "bg-yellow-500", error: "bg-destructive", not_synced: "bg-muted-foreground",
};

const STATUSES = ["Draft", "Sent", "Accepted", "Rejected", "Closed"];

export type UnifiedEstimate = {
  source: "cmdx" | "qb";
  id: string;
  number: string | null;
  customerName: string | null;
  date: string | null;
  expiryDate?: string | null;
  status: string;
  syncStatus?: string | null;
  totalAmount: number | null;
  notes?: string | null;
  lineItems: any[];
  qbId?: string | null;
  // Tracking fields
  sentAt?: string | null;
  sentToName?: string | null;
  sentToEmail?: string | null;
  viewedAt?: string | null;
  viewCount?: number;
  approvedAt?: string | null;
  approvedByName?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  customerNotes?: string | null;
  shareToken?: string | null;
};

export function unifyEstimate(e: Estimate): UnifiedEstimate {
  const raw = e as any;
  return {
    source: "cmdx", id: e.id, number: e.estimate_number, customerName: e.customers?.name ?? null,
    date: e.txn_date, expiryDate: e.expiry_date, status: e.status, syncStatus: e.qb_sync_status,
    totalAmount: e.total_amount, notes: e.notes, lineItems: e.line_items ?? [], qbId: e.qb_estimate_id,
    sentAt: raw.sent_at, sentToName: raw.sent_to_name, sentToEmail: raw.sent_to_email,
    viewedAt: raw.viewed_at, viewCount: raw.view_count ?? 0,
    approvedAt: raw.approved_at, approvedByName: raw.approved_by_name,
    rejectedAt: raw.rejected_at, rejectionReason: raw.rejection_reason,
    customerNotes: raw.customer_notes, shareToken: raw.share_token,
  };
}

export function unifyQBEstimate(e: QBEstimate): UnifiedEstimate {
  const st = e.txn_status || e.status || "Pending";
  return {
    source: "qb", id: e.id, number: e.doc_number, customerName: e.customer_ref_name || e.customer_qb_id,
    date: e.txn_date, status: st, totalAmount: e.total_amount, lineItems: (e.line_items as any[]) ?? [], qbId: e.quickbooks_id,
  };
}

interface Props {
  estimate: UnifiedEstimate | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onConvert?: () => void;
  onEdit?: () => void;
  rawEstimate?: Estimate | null;
}

export const EstimateDetailDrawer = ({ estimate, open, onOpenChange, onConvert, onEdit, rawEstimate }: Props) => {
  const [viewMode, setViewMode] = useState<"internal" | "customer">("internal");
  const [sendOpen, setSendOpen] = useState(false);
  const updateEstimate = useUpdateEstimate();

  if (!estimate) return null;

  const canConvert = (estimate.source === "cmdx" && estimate.status === "Accepted") || (estimate.source === "qb" && ["Accepted", "Closed"].includes(estimate.status));
  const canSend = estimate.source === "cmdx" && ["Draft", "Sent"].includes(estimate.status);

  // Calculate totals
  let laborCost = 0, laborBilled = 0, materialCost = 0, materialBilled = 0;
  estimate.lineItems.forEach((item: any) => {
    if (isNewFormat(item)) {
      laborBilled += item.labor_total_billed ?? item.labor_total ?? 0;
      materialBilled += item.material_total_billed ?? item.material_total ?? 0;
      laborCost += item.labor_total_cost ?? 0;
      materialCost += item.material_total_cost ?? 0;
    } else if (Array.isArray(item.sub_lines)) {
      (item.sub_lines as SubLine[]).forEach((sl) => {
        const billed = sl.total_billed ?? sl.total ?? 0;
        const cost = sl.total_cost ?? 0;
        if (sl.type === "labor") { laborBilled += billed; laborCost += cost; }
        else { materialBilled += billed; materialCost += cost; }
      });
    } else {
      const qty = item.qty ?? item.Qty ?? 0;
      const lp = item.labor_price ?? 0;
      const mp = item.material_price ?? 0;
      const up = item.UnitPrice ?? item.unit_price ?? 0;
      if (item.line_type === "labor") laborBilled += qty * (lp || up);
      else if (item.line_type === "material") materialBilled += qty * (mp || up);
      else { laborBilled += qty * lp; materialBilled += qty * mp; if (!lp && !mp) laborBilled += qty * up; }
    }
  });

  const totalCost = laborCost + materialCost;
  const totalBilled = laborBilled + materialBilled;
  const profit = totalBilled - totalCost;
  const margin = totalBilled > 0 ? (profit / totalBilled) * 100 : 0;
  const marginColor = margin >= 30 ? "text-emerald-600" : margin >= 20 ? "text-yellow-600" : "text-destructive";

  const handleStatusChange = async (newStatus: string) => {
    if (estimate.source !== "cmdx") return;
    try {
      await updateEstimate.mutateAsync({ id: estimate.id, status: newStatus } as any);
      toast.success(`Status updated to ${newStatus}`);
    } catch (err: any) {
      toast.error(err.message || "Failed to update status");
    }
  };

  const copyShareLink = () => {
    if (estimate.shareToken) {
      navigator.clipboard.writeText(`${window.location.origin}/estimate/${estimate.shareToken}`);
      toast.success("Link copied!");
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-2xl w-full overflow-y-auto">
          <SheetHeader className="pb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <SheetTitle className="flex items-center gap-2">
                {estimate.source === "qb" ? (
                  <Badge variant="outline" className="bg-muted text-muted-foreground text-xs font-mono">QB {estimate.number}</Badge>
                ) : (
                  <Badge className="bg-primary text-primary-foreground text-xs font-mono">{estimate.number}</Badge>
                )}
              </SheetTitle>
              <Badge variant="outline" className={statusClass[estimate.status] ?? "bg-muted text-muted-foreground"}>{estimate.status}</Badge>
              {estimate.syncStatus && (
                <div className="flex items-center gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${syncDotClass[estimate.syncStatus] ?? syncDotClass.not_synced}`} />
                  <span className="text-xs text-muted-foreground capitalize">{estimate.syncStatus}</span>
                </div>
              )}
            </div>
            <div className="flex justify-end mt-2">
              <div className="inline-flex rounded-md border border-border overflow-hidden text-sm">
                <button className={cn("px-3 py-1", viewMode === "internal" ? "bg-primary text-primary-foreground" : "bg-background text-foreground hover:bg-muted")} onClick={() => setViewMode("internal")}>Internal</button>
                <button className={cn("px-3 py-1 border-l border-border", viewMode === "customer" ? "bg-primary text-primary-foreground" : "bg-background text-foreground hover:bg-muted")} onClick={() => setViewMode("customer")}>Customer</button>
              </div>
            </div>
          </SheetHeader>

          <div className="space-y-6">
            {/* Approved Banner */}
            {estimate.status === "Accepted" && estimate.approvedByName && (
              <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span className="font-semibold text-emerald-800 dark:text-emerald-400">Approved by {estimate.approvedByName}</span>
                </div>
                <p className="text-sm text-muted-foreground">{safeDateFull(estimate.approvedAt ?? null)}</p>
                {estimate.customerNotes && <p className="text-sm italic">"{estimate.customerNotes}"</p>}
                {canConvert && onConvert && (
                  <Button onClick={onConvert} className="mt-2">
                    🚀 Convert to Project <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                )}
              </div>
            )}

            {/* Rejected Banner */}
            {estimate.status === "Rejected" && (
              <div className="p-4 rounded-lg border border-destructive/30 bg-destructive/5 space-y-1">
                <p className="font-semibold text-destructive">❌ Rejected by {estimate.approvedByName}</p>
                <p className="text-sm text-muted-foreground">{safeDateFull(estimate.rejectedAt ?? null)}</p>
                {estimate.rejectionReason && <p className="text-sm italic">"{estimate.rejectionReason}"</p>}
              </div>
            )}

            {/* Tracking Panel */}
            {estimate.sentAt && (
              <div className="p-3 rounded-lg border bg-muted/30 space-y-2 text-sm">
                <p className="font-semibold flex items-center gap-1"><Send className="h-3 w-3" /> Sent to Customer</p>
                <p className="text-muted-foreground">{estimate.sentToName} — {estimate.sentToEmail}</p>
                <p className="text-muted-foreground">Sent: {safeDateFull(estimate.sentAt)}</p>
                {estimate.viewedAt && (
                  <p className="flex items-center gap-1 text-muted-foreground">
                    <Eye className="h-3 w-3" /> Viewed: {safeDateFull(estimate.viewedAt)}
                    {(estimate.viewCount ?? 0) > 1 && <span>(viewed {estimate.viewCount} times)</span>}
                  </p>
                )}
                {estimate.status === "Sent" && (
                  <p className="flex items-center gap-1 text-muted-foreground">
                    <Clock className="h-3 w-3" /> Awaiting response
                  </p>
                )}
                <div className="flex gap-2 mt-1">
                  {canSend && <Button variant="outline" size="sm" onClick={() => setSendOpen(true)}>Resend</Button>}
                  {estimate.shareToken && <Button variant="outline" size="sm" onClick={copyShareLink}><Copy className="h-3 w-3 mr-1" /> Copy Link</Button>}
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4 text-sm">
              <div><span className="text-muted-foreground">Customer</span><p className="font-medium">{estimate.customerName ?? "—"}</p></div>
              <div><span className="text-muted-foreground">Date</span><p className="font-medium">{safeDate(estimate.date)}</p></div>
              <div><span className="text-muted-foreground">Expiry</span><p className="font-medium">{safeDate(estimate.expiryDate ?? null)}</p></div>
            </div>

            <Separator />

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Line Items</h3>
              {estimate.lineItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">No line items</p>
              ) : (
                estimate.lineItems.map((item: any, idx: number) => {
                  const hasSubLines = Array.isArray(item.sub_lines) && item.sub_lines.length > 0;
                  const desc = item.description || item.Description || `Line ${idx + 1}`;
                  const custDesc = item.customer_description || desc;
                  const qty = item.qty ?? item.Qty ?? 0;
                  const unit = item.unit || item.UnitOfMeasure || "";
                  const lineBilled = item.total_billed ?? item.total ?? item.Amount ?? (qty * (item.UnitPrice ?? item.unit_price ?? 0));

                  if (viewMode === "customer") {
                    const combinedUp = hasSubLines ? (item.sub_lines as any[]).reduce((s: number, sl: any) => s + (sl.unit_price ?? 0), 0) : (lineBilled / (qty || 1));
                    return (
                      <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground font-mono w-6">#{item.line_num ?? idx + 1}</span>
                          <span className="font-medium text-sm">{custDesc}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-muted-foreground">{qty} {unit}</span>
                          <span className="text-muted-foreground">@ {fmt(combinedUp)}</span>
                          <span className="font-semibold">{fmt(lineBilled)}</span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={idx} className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground font-mono w-6">#{item.line_num ?? idx + 1}</span>
                          <span className="font-medium text-sm">{desc}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-muted-foreground">{qty} {unit}</span>
                          <span className="font-semibold">{fmt(lineBilled)}</span>
                        </div>
                      </div>
                      {hasSubLines && (item.sub_lines as any[]).map((sl: any, si: number) => {
                        const slCost = sl.cost_price ?? 0;
                        const slBilled = sl.total_billed ?? sl.total ?? 0;
                        const slMarkup = sl.markup_pct ?? 0;
                        return (
                          <div key={sl.id ?? si} className="space-y-0.5">
                            <div className="flex items-center gap-2 pl-8 text-sm">
                              <Badge variant="outline" className={`text-xs ${sl.type === "labor" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                                {sl.type === "labor" ? "Labor" : "Matl"}
                              </Badge>
                              <span className="flex-1 truncate">{sl.description}</span>
                              <span className="text-muted-foreground">{sl.qty} {sl.unit}</span>
                              {slCost > 0 && <span className="text-muted-foreground text-xs">Cost: {fmt(slCost)}</span>}
                              {slMarkup > 0 && <span className="text-muted-foreground text-xs">{slMarkup}%</span>}
                              <span className="text-muted-foreground">@ {fmt(sl.unit_price)}</span>
                              <span className="font-medium">{fmt(slBilled)}</span>
                            </div>
                            {slCost > 0 && (
                              <p className="text-[11px] text-muted-foreground pl-8 ml-[52px]">
                                Cost: {fmt(slCost)} → Billed: {fmt(sl.unit_price)} ({slMarkup}% markup)
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              )}
            </div>

            {viewMode === "internal" && totalCost > 0 && (
              <>
                <Separator />
                <div className="border rounded-lg p-3 space-y-1 bg-yellow-50/50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800">
                  <div className="flex items-center gap-1 text-xs font-semibold text-yellow-700 dark:text-yellow-400 mb-1">
                    <AlertTriangle className="h-3 w-3" /> INTERNAL — Not shown to customer
                  </div>
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total Cost</span><span>{fmt(totalCost)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total Revenue</span><span>{fmt(totalBilled)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Gross Profit</span><span className={marginColor}>{fmt(profit)}</span></div>
                  <div className="flex justify-between text-sm font-semibold"><span className="text-muted-foreground">Margin</span><span className={marginColor}>{margin.toFixed(1)}%</span></div>
                </div>
              </>
            )}

            {estimate.notes && (
              <><Separator /><div><h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1">Customer Notes</h3><p className="text-sm whitespace-pre-wrap">{estimate.notes}</p></div></>
            )}

            <Separator />

            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Labor (billed)</span><span>{fmt(laborBilled)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Material (billed)</span><span>{fmt(materialBilled)}</span></div>
              <div className="flex justify-between font-semibold text-base border-t pt-2 mt-2">
                <span>Grand Total</span><span>{fmt(estimate.totalAmount)}</span>
              </div>
            </div>

            <Separator />

            <div className="flex items-center gap-2 flex-wrap">
              {estimate.source === "cmdx" && estimate.status === "Draft" && onEdit && (
                <Button variant="outline" onClick={onEdit}>
                  <Pencil className="mr-1 h-4 w-4" /> Edit Estimate
                </Button>
              )}
              {canSend && (
                <Button variant="outline" onClick={() => setSendOpen(true)}>
                  <Send className="mr-1 h-4 w-4" /> Send to Customer
                </Button>
              )}
              {canConvert && !estimate.approvedByName && (
                <Button onClick={onConvert}>Convert to Project <ArrowRight className="ml-1 h-4 w-4" /></Button>
              )}
              {estimate.qbId && (
                <Button variant="outline" asChild>
                  <a href={`https://app.qbo.intuit.com/app/estimate?txnId=${estimate.qbId}`} target="_blank" rel="noopener noreferrer">
                    View in QB <ExternalLink className="ml-1 h-4 w-4" />
                  </a>
                </Button>
              )}
              {estimate.source === "cmdx" && (
                <Select value={estimate.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <SendEstimateDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        estimate={rawEstimate ?? null}
        onSent={() => {
          setSendOpen(false);
          // Refresh will happen via query invalidation
        }}
      />
    </>
  );
};
